import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("vlx_admin_token") || ""}` });
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const METHODS = ["zelle", "check", "cash", "card", "wire", "other"];
const today = () => new Date().toISOString().slice(0, 10);

export const PaymentsPanel = ({ invoiceId, payments, total, depositAmount, onChange }) => {
  const paid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const balance = Math.max(total - paid, 0);
  const [draft, setDraft] = useState({ amount: "", label: payments.length ? "Payment" : "Deposit", method: "zelle", date: today(), note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const record = async (override) => {
    const body = { ...draft, ...override };
    const amount = Number(body.amount);
    if (!amount || amount <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/invoices/${invoiceId}/payments`, { ...body, amount, note: body.note || null }, { headers: auth() });
      onChange(data);
      setDraft({ amount: "", label: "Payment", method: body.method, date: today(), note: "" });
      toast.success(`${body.label} of ${money(amount)} recorded`);
    } catch { toast.error("Could not record payment"); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    try {
      const { data } = await axios.delete(`${API}/invoices/${invoiceId}/payments/${p.id}`, { headers: auth() });
      onChange(data);
      toast.success("Payment removed");
    } catch { toast.error("Could not remove payment"); }
  };

  return (
    <section data-testid="payments-panel" className="bg-white border border-[#c99658]/25 p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f]">Payments</h4>
        <div className="text-sm">
          <span className="text-neutral-600">Paid </span><span data-testid="payments-paid" className="font-semibold">{money(paid)}</span>
          <span className="text-neutral-400 mx-2">·</span>
          <span className="text-neutral-600">Balance due </span><span data-testid="payments-balance" className={`font-semibold ${balance <= 0 ? "text-emerald-700" : "text-[#0f2420]"}`}>{money(balance)}</span>
        </div>
      </div>

      {payments.length > 0 && (
        <ul className="divide-y divide-[#c99658]/15 text-sm">
          {payments.map((p) => (
            <li key={p.id} data-testid={`payment-${p.id}`} className="py-1.5 flex items-center gap-3">
              <span className="flex-1 min-w-0">
                <span className="font-medium">{p.label}</span>
                <span className="text-neutral-500 text-[12px]"> · {p.date}{p.method ? ` · ${p.method}` : ""}{p.note ? ` · ${p.note}` : ""}</span>
              </span>
              <span className="tabular-nums">{money(p.amount)}</span>
              <button data-testid={`payment-delete-${p.id}`} onClick={() => remove(p)} aria-label="Remove payment" className="text-neutral-400 hover:text-red-600"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}

      {balance > 0 && (
        <div className="space-y-2 pt-1">
          <div className="flex gap-2 flex-wrap">
            {paid === 0 && depositAmount > 0 && (
              <button data-testid="quick-deposit" disabled={busy} onClick={() => record({ amount: depositAmount, label: "Deposit" })}
                className="px-3 h-8 bg-[#c99658] text-[#0f2420] text-[10px] tracking-[0.2em] uppercase font-raleway hover:bg-[#7a5b2f] hover:text-white disabled:opacity-50">
                Deposit paid · {money(depositAmount)}
              </button>
            )}
            <button data-testid="quick-full" disabled={busy} onClick={() => record({ amount: balance, label: paid > 0 ? "Final payment" : "Payment" })}
              className="px-3 h-8 border border-[#0f2420] text-[#0f2420] text-[10px] tracking-[0.2em] uppercase font-raleway hover:bg-[#0f2420] hover:text-white disabled:opacity-50">
              Paid in full · {money(balance)}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end">
            <label className="text-[10px] tracking-[0.15em] uppercase font-raleway text-neutral-500">Amount
              <input data-testid="payment-amount" type="number" step="0.01" value={draft.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm bg-white" />
            </label>
            <label className="text-[10px] tracking-[0.15em] uppercase font-raleway text-neutral-500">Type
              <select data-testid="payment-label" value={draft.label} onChange={(e) => set("label", e.target.value)} className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm bg-white">
                {["Deposit", "Payment", "Final payment"].map((l) => <option key={l}>{l}</option>)}
              </select>
            </label>
            <label className="text-[10px] tracking-[0.15em] uppercase font-raleway text-neutral-500">Method
              <select data-testid="payment-method" value={draft.method} onChange={(e) => set("method", e.target.value)} className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm bg-white">
                {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="text-[10px] tracking-[0.15em] uppercase font-raleway text-neutral-500">Date
              <input data-testid="payment-date" type="date" value={draft.date} onChange={(e) => set("date", e.target.value)} className="mt-1 w-full border border-neutral-300 px-2 py-1.5 text-sm bg-white" />
            </label>
            <button data-testid="payment-record" disabled={busy} onClick={() => record()} className="col-span-2 sm:col-span-1 h-[34px] px-4 bg-[#0f2420] text-white text-[10px] tracking-[0.2em] uppercase font-raleway hover:bg-[#7a5b2f] disabled:opacity-50">Record</button>
          </div>
        </div>
      )}
      {balance <= 0 && payments.length > 0 && <p data-testid="payments-paid-in-full" className="text-emerald-700 text-sm font-medium">Paid in full — thank you.</p>}
    </section>
  );
};
